import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  SettingName,
  type PropertySharingExternalSite,
} from 'src/api/generated';
import { useSettings, useUpdateSetting } from 'src/hooks/api/settings';
import {
  usePropertySharingSetup,
  usePropertySharingParameters,
} from 'src/modules/shared/hooks/api/catalog/property-sharing';
import type {
  PropertyLinksFormValues,
  UsePropertyLinksSettingsOptions,
} from '../types/property-links.types';
import { propertyLinksFormSchema } from '../validations/property-links.validation';

/**
 * Read tenant link settings and expose complete settings actions to the UI.
 * @param options - Current access and the callback for a completed save.
 * @returns Parsed policy, discovery state, save feedback and settings actions.
 */
export function usePropertyLinksSettings({
  canRead,
  canUpdate,
  canReadProperties,
  onSaved,
}: UsePropertyLinksSettingsOptions) {
  const client = useQueryClient();
  const settings = useSettings(canRead);
  const setup = usePropertySharingSetup(canRead);
  const parameters = usePropertySharingParameters(canRead && canReadProperties);
  const stored = settings.settings.find(
    ({ name }) => name === SettingName.PROPERTY_SHARING_LINKS
  );
  const savedPolicy = useMemo(
    () => parsePolicy(stored?.value),
    [stored?.value]
  );
  const defaultPolicy = useMemo(
    () => parsePolicy(stored?.defaultValue),
    [stored?.defaultValue]
  );
  const policy = savedPolicy ?? defaultPolicy;
  const invalidStoredValue = stored?.hasInvalidValue === true || !savedPolicy;
  const validation = useMemo(
    () => propertyLinksFormSchema.safeParse(policy),
    [policy]
  );
  const save = useUpdateSetting(() => {
    void client.invalidateQueries({ queryKey: ['catalog/property-sharing'] });
    void client.invalidateQueries({ queryKey: ['catalog/properties'] });
    onSaved();
  });
  const savePolicy = (next: PropertyLinksFormValues | null) => {
    if (!next || !canUpdate || save.loading) return;
    save.updateSetting({
      name: SettingName.PROPERTY_SHARING_LINKS,
      value: JSON.stringify(next),
      scope: 'tenant',
    });
  };
  const moveDestination = (index: number, offset: number) => {
    if (!policy || settings.error) return;
    const priority = [...policy.priority];
    [priority[index], priority[index + offset]] = [
      priority[index + offset],
      priority[index],
    ];
    savePolicy({ ...policy, priority });
  };
  const saveExternalSite = (
    externalSite: PropertySharingExternalSite | null
  ) => {
    if (policy) savePolicy({ ...policy, externalSite });
  };

  return {
    policy,
    validation,
    invalidStoredValue,
    canReset: Boolean(
      defaultPolicy &&
        (invalidStoredValue ||
          JSON.stringify(policy) !== JSON.stringify(defaultPolicy))
    ),
    hasDefaultPolicy: defaultPolicy !== null,
    loading: settings.loading || setup.isPending,
    loadError: setup.isError || !policy,
    refreshError: settings.error,
    comosWebsiteAvailable: setup.data?.comosWebsiteAvailable ?? false,
    identifiers:
      parameters.data?.pages.flatMap((page) => page.identifiers) ?? [],
    hasMoreIdentifiers: parameters.hasNextPage,
    loadingMoreIdentifiers: parameters.isFetchingNextPage,
    identifiersError: parameters.isError,
    loadMoreIdentifiers: () => {
      void parameters.fetchNextPage();
    },
    reloadIdentifiers: () => {
      void parameters.refetch();
    },
    reload: () => {
      void settings.refetch();
      void setup.refetch();
    },
    moveDestination,
    saveExternalSite,
    removeExternalSite: () => saveExternalSite(null),
    resetToDefault: () => savePolicy(defaultPolicy),
    saving: save.loading,
    saved: save.success,
    saveError: save.error,
    retrySave: () => {
      if (canUpdate) save.retry();
    },
    resetSaveFeedback: save.reset,
  };
}

/** Parse stored values and backend defaults without duplicating the application policy. */
function parsePolicy(
  value: string | undefined
): PropertyLinksFormValues | null {
  try {
    return propertyLinksFormSchema.parse(JSON.parse(value ?? ''));
  } catch {
    return null;
  }
}
